import React from 'react'

import {
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Icon,
  IconVariant,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { RecipeGroupedItemDetails } from './RecipeGroupedItemDetails'
import { RecipeGroupedItemFooter } from './RecipeGroupedItemFooter'
import type { RecipeGroupedItemProps } from './types'

const RecipeCard = withCitrusTheme(Box, (theme) => ({
  '@keyframes restoredRecipeHighlight': {
    '0%': {
      backgroundColor: theme.color(Color.White),
      boxShadow: '0 1px 6px rgba(0, 0, 0, 0.12)',
    },
    '15%, 70%': {
      backgroundColor: theme.color(Color.Primary_50),
      boxShadow: `0 0 0 2px ${theme.color(Color.Primary_900)}, 0 2px 10px rgba(0, 0, 0, 0.14)`,
    },
    '100%': {
      backgroundColor: theme.color(Color.White),
      boxShadow: '0 1px 6px rgba(0, 0, 0, 0.12)',
    },
  },
  '&[data-highlighted="true"]': {
    animation: 'restoredRecipeHighlight 2400ms cubic-bezier(0.22, 1, 0.36, 1) both',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&[data-highlighted="true"]': {
      animation: 'none',
      backgroundColor: theme.color(Color.Primary_50),
      boxShadow: `0 0 0 2px ${theme.color(Color.Primary_900)}, 0 1px 6px rgba(0, 0, 0, 0.12)`,
    },
  },
}))

export const RecipeGroupedItem = ({
  isHighlighted = false,
  item,
  pendingOperation,
  onClickRecipe,
  onEditRecipe,
  onRemoveRecipe,
}: RecipeGroupedItemProps) => {
  const isPending = Boolean(pendingOperation)
  const isPortionUpdatePending = pendingOperation === 'updating-portions'
  const handleRemoveClick = (event: React.MouseEvent) => {
    event.stopPropagation()

    if (isPending) {
      return
    }

    onRemoveRecipe(item.id)
  }
  const stopRemoveKeyPropagation = (event: React.KeyboardEvent) => {
    event.stopPropagation()
  }

  return (
    <RecipeCard
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      color={Color.ColdGrey_900}
      data-highlighted={isHighlighted || undefined}
      data-testid="recipe-grouped-item"
      aria-busy={isPending || undefined}
      style={{ borderRadius: '6px', overflow: 'hidden' }}
    >
      <Box display={Display.Flex} justifyContent={JustifyContent.FlexStart}>
        <RecipeGroupedItemDetails
          item={item}
          isPending={isPending}
          showSkeleton={isPortionUpdatePending}
          onClickRecipe={onClickRecipe}
        />
        <Box
          display={Display.Flex}
          justifyContent={JustifyContent.FlexEnd}
          minWidth="40px"
          paddingRight={2}
          style={{ flexShrink: 0, paddingTop: '0.35rem' }}
        >
          <Button
            aria-label={`Remove ${item.title}`}
            colorVariant={ButtonColorVariant.Tertiary}
            disabled={isPending}
            onClick={handleRemoveClick}
            onKeyDown={stopRemoveKeyPropagation}
            onKeyUp={stopRemoveKeyPropagation}
            width="40px"
            style={{ height: '40px', minHeight: '40px', minWidth: '40px', padding: 0 }}
          >
            <Icon name="delete" variant={IconVariant.Actionable} size={5} />
          </Button>
        </Box>
      </Box>
      <RecipeGroupedItemFooter
        item={item}
        pendingOperation={pendingOperation}
        onEditRecipe={onEditRecipe}
      />
    </RecipeCard>
  )
}
